import React, { ContextType, useMemo } from "react";

import { Box, CardMedia, IconButton, Paper, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import clsx from "clsx";

import Price from "components/Price/Price";
import CartContext from "contexts/CartContext";
import useCart from "hooks/useCart";
import useLocalize from "hooks/useLocalize";
import useTranslation from "hooks/useTranslation";
import CloseCircle from "icons/CloseCircle";
import EditCircle from "icons/EditCircle";

const useStyles = makeStyles(theme => {
	return {
		card: {
			backgroundColor: "transparent",
			display: "flex",
			width: "100%",
			paddingTop: theme.spacing(0.5),
			paddingBottom: theme.spacing(0.5),
			margin: theme.spacing(0.5),
			paddingRight: theme.spacing(1),
			color: "#fff",
			borderRadius: 0
		},
		cardAction: {
			minHeight: 70,
			width: "100%",
			paddingTop: theme.spacing(1),
			paddingBottom: theme.spacing(1),
			paddingLeft: theme.spacing(4),
			display: "flex",
			flexDirection: "column",
			justifyContent: "space-between",
			position: "relative",
			borderBottom: "1px solid #fff"
		},
		icon: {
			fill: "#fff",
			[theme.breakpoints.up(480)]: {
				fill: theme.palette.text.primary
			}
		},
		item: {
			display: "flex",
			flexDirection: "row",
			alignContent: "center",
			alignItems: "center"
		},
		itemCode: {
			fontSize: 12,
			marginRight: theme.spacing(1)
		},
		itemName: {
			top: 10,
			fontSize: 12,
			fontWeight: 500,
			color: "#f2f2f2",
			textTransform: "uppercase",
			textOverflow: "ellipsis"
		},
		optionRow: {
			paddingTop: theme.spacing(1),
			paddingLeft: theme.spacing(2)
		},
		option: {
			fontSize: 12,
			textTransform: "uppercase",
			color: "#a4a4a4"
		},
		instructions: {
			padding: theme.spacing(2),
			fontSize: 12,
			textTransform: "uppercase",
			color: "#a4a4a4"
		},
		price: {
			fontSize: 12
		},
		itemRow: {
			width: "100%",
			display: "flex",
			fontWeight: 500,
			flexDirection: "row",
			alignContent: "center",
			alignItems: "center",
			justifyContent: "space-between"
		},
		totalRow: {
			width: "100%",
			display: "flex",
			flexDirection: "row",
			alignContent: "flex-end",
			justifyContent: "flex-end"
		},
		total: {
			fontSize: 12,
			fontWeight: 500
		},
		remove: {
			position: "absolute",
			top: theme.spacing(0.5),
			left: 15,
			flex: 0.1,
			padding: 5,
			marginLeft: -10,
			textAlign: "left",
			alignSelf: "flex-start"
		},
		edit: {
			position: "absolute",
			bottom: theme.spacing(0.5),
			left: 15,
			flex: 0.1,
			padding: 5,
			marginLeft: -10,
			textAlign: "left",
			alignSelf: "flex-start"
		},
		qty: {
			fontSize: 12,
			marginRight: theme.spacing(1)
		}
	};
});

interface CartItemProps {
	props?: any;
	cartItem: ContextType<typeof CartContext>["cartItems"][number];
}

const CartItemMobile: React.FC<CartItemProps> = ({ cartItem }) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const { removeFromCart } = useCart();

	const { menuItem } = cartItem;

	const handleRemoveClick = useMemo(() => () => removeFromCart(cartItem), [cartItem, removeFromCart]);

	const handleEditClick = useMemo(() => () => {}, []);

	const selectedOptions: any[] = [];
	if (cartItem.selectedOptions.length > 0) {
		cartItem.selectedOptions.map(s => {
			const itemOption = menuItem.menuItemOptionGroups
				.find(g => g.id === s.menuItemOptionGroupId)
				?.menuItemOptions.find(o => o.id === s.menuItemOptionId);
			selectedOptions.push(itemOption);
		});
	}

	return (
		<Paper elevation={0} className={classes.card}>
			<Box className={classes.cardAction}>
				<Box className={classes.itemRow}>
					<IconButton className={classes.remove} onClick={handleRemoveClick} title={t("remove")}>
						<CloseCircle className={classes.icon} />
					</IconButton>
					<Box className={classes.item}>
						{menuItem.itemCode && <Typography className={classes.itemCode}>{menuItem.itemCode}</Typography>}
						<Typography className={classes.itemName}>{localize(menuItem.localeNames, menuItem.name)}</Typography>
					</Box>
					{
						// <Price className={classes.price} priceCents={cartItem.extendedPriceCents} />
					}
				</Box>
				{selectedOptions.length > 0 &&
					selectedOptions.map(o => (
						<Box key={o.id} className={clsx(classes.item, classes.optionRow)}>
							{o.itemCode && <Typography className={clsx(classes.itemCode, classes.option)}>{o.itemCode}</Typography>}
							<Typography className={clsx(classes.itemName, classes.option)}>
								{localize(o.localeNames, o.name)}
							</Typography>
						</Box>
					))}

				{cartItem.specialInstructions && <Box className={classes.instructions}>{cartItem.specialInstructions}</Box>}
				<Box className={classes.totalRow}>
					<IconButton
						component="a"
						href={`#cart:${cartItem.localId}`}
						className={classes.edit}
						onClick={handleEditClick}
						title={t("edit")}
					>
						<EditCircle width={16} height={16} className={classes.icon} />
					</IconButton>
					{cartItem.quantity > 1 ? (
						<Typography className={classes.qty}>{`${t("qty")} ${cartItem.quantity}`}&nbsp;&nbsp;-</Typography>
					) : null}
					<Price className={classes.total} priceCents={cartItem.extendedPriceCents} />
				</Box>
			</Box>
		</Paper>
	);
};

export default CartItemMobile;
